/**
 * Chart colours and axis defaults resolved from the design tokens, shared by
 * the uPlot charts. The tokens are read when a chart is built, from the
 * document root, which carries the theme (standard or night) and the density
 * the UI store holds; useChartColours re-reads them whenever either setting
 * changes, so open charts rebuild in the new palette.
 */
import { useMemo } from 'react';
import type uPlot from 'uplot';
import { useUI } from '@/state/store';

export interface ChartColours {
  ink: string;
  ink2: string;
  ink3: string;
  grid: string;
  accent: string;
  nominal: string;
  caution: string;
  warning: string;
  live: string;
  sim: string;
  raised: string;
}

const FALLBACK: ChartColours = {
  ink: '#eef0f4',
  ink2: '#aeb4c2',
  ink3: '#7c8496',
  grid: 'rgba(255,255,255,0.08)',
  accent: '#deff5f',
  nominal: '#1f9e89',
  caution: '#e0a100',
  warning: '#e1462c',
  live: '#35d19a',
  sim: '#8a93a6',
  raised: '#1d2029',
};

function cssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** Token colours resolved from :root now (Tailwind v4 publishes @theme as CSS variables; the night theme overrides them under data-theme). */
export function chartColours(): ChartColours {
  return {
    ink: cssVar('--color-ink', FALLBACK.ink),
    ink2: cssVar('--color-ink-2', FALLBACK.ink2),
    ink3: cssVar('--color-ink-3', FALLBACK.ink3),
    grid: FALLBACK.grid,
    accent: cssVar('--color-accent', FALLBACK.accent),
    nominal: cssVar('--color-nominal', FALLBACK.nominal),
    caution: cssVar('--color-caution', FALLBACK.caution),
    warning: cssVar('--color-warning', FALLBACK.warning),
    live: cssVar('--color-live', FALLBACK.live),
    sim: cssVar('--color-sim', FALLBACK.sim),
    raised: cssVar('--color-raised', FALLBACK.raised),
  };
}

/**
 * The chart palette for the current appearance. The UI store writes the theme
 * and density onto the root in its own subscriber, which runs before React
 * re-renders, so when either setting changes the tokens read here are
 * already the new ones; the palette is a new object then, and a chart whose
 * options depend on it rebuilds (UPlotChart rebuilds on new options).
 */
export function useChartColours(): ChartColours {
  const theme = useUI((s) => s.theme);
  const density = useUI((s) => s.density);
  // theme and density are what the root's tokens follow; chartColours reads the root, not them.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => chartColours(), [theme, density]);
}

export const CHART_FONT = '10px "JetBrains Mono Variable", ui-monospace, monospace';

/** Axis defaults for the dark surface. */
export function axisDefaults(c: ChartColours): Partial<uPlot.Axis> {
  return {
    stroke: c.ink3,
    font: CHART_FONT,
    labelFont: CHART_FONT,
    grid: { show: true, stroke: c.grid, width: 1 },
    ticks: { show: true, stroke: c.grid, width: 1, size: 4 },
    gap: 4,
  };
}

/** "#rrggbb" to rgba(); other strings pass through. */
export function withAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
